import React, { useState, useEffect } from 'react';
import { useCompany } from '../../contexts/CompanyContext';
import {
  Box,
  Typography,
  Paper,
  Switch,
  FormControlLabel,
  TextField,
  Button,
  Alert,
  Divider,
  Grid,
  InputAdornment
} from '@mui/material';
import {
  Email as EmailIcon,
  CalendarToday as CalendarIcon,
  Receipt as ReceiptIcon,
  Work as WorkIcon
} from '@mui/icons-material';

export default function AutomationSettings() {
  const { companyProfile, updateCompanyProfile, loading } = useCompany();
  const [formData, setFormData] = useState({
    autoCreateJobFromApproved: companyProfile?.automationSettings?.autoCreateJobFromApproved ?? true,
    autoSendAppointmentReminders: companyProfile?.automationSettings?.autoSendAppointmentReminders ?? false,
    appointmentReminderHours: companyProfile?.automationSettings?.appointmentReminderHours ?? 24,
    autoSendEstimateFollowUp: companyProfile?.automationSettings?.autoSendEstimateFollowUp ?? false,
    estimateFollowUpDays: companyProfile?.automationSettings?.estimateFollowUpDays ?? 3,
    autoGenerateInvoiceOnCompletion: companyProfile?.automationSettings?.autoGenerateInvoiceOnCompletion ?? false
  });
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (companyProfile?.automationSettings) {
      setFormData({
        autoCreateJobFromApproved: companyProfile.automationSettings.autoCreateJobFromApproved ?? true,
        autoSendAppointmentReminders: companyProfile.automationSettings.autoSendAppointmentReminders ?? false,
        appointmentReminderHours: companyProfile.automationSettings.appointmentReminderHours ?? 24,
        autoSendEstimateFollowUp: companyProfile.automationSettings.autoSendEstimateFollowUp ?? false,
        estimateFollowUpDays: companyProfile.automationSettings.estimateFollowUpDays ?? 3,
        autoGenerateInvoiceOnCompletion: companyProfile.automationSettings.autoGenerateInvoiceOnCompletion ?? false
      });
    }
  }, [companyProfile]);

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);

    try {
      await updateCompanyProfile({
        ...companyProfile,
        automationSettings: {
          autoCreateJobFromApproved: formData.autoCreateJobFromApproved,
          autoSendAppointmentReminders: formData.autoSendAppointmentReminders,
          appointmentReminderHours: formData.appointmentReminderHours,
          autoSendEstimateFollowUp: formData.autoSendEstimateFollowUp,
          estimateFollowUpDays: formData.estimateFollowUpDays,
          autoGenerateInvoiceOnCompletion: formData.autoGenerateInvoiceOnCompletion
        }
      });
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (error) {
      console.error('Error updating automation settings:', error);
      setError(error.message || 'Failed to update automation settings. Please try again.');
    }
  };

  if (loading) {
    return <Typography>Loading...</Typography>;
  }

  return (
    <Paper elevation={3} sx={{ p: 3, maxWidth: 800, mx: 'auto', mt: 4 }}>
      <Typography variant="h5" gutterBottom>
        Automation Settings
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Configure automated workflows to save time and improve customer communication.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(false)}>
          Automation settings saved successfully!
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        {/* Auto-create Job from Approved Estimate */}
        <Paper sx={{ p: 3, mb: 3, bgcolor: 'background.default' }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
              <WorkIcon color="primary" />
              <Box>
                <Typography variant="h6" gutterBottom>
                  Auto-Create Job from Approved Estimate
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Automatically create a job when an estimate is approved or signed by a customer.
                </Typography>
              </Box>
            </Box>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.autoCreateJobFromApproved}
                  onChange={(e) => handleChange('autoCreateJobFromApproved', e.target.checked)}
                  color="primary"
                />
              }
              label={formData.autoCreateJobFromApproved ? 'Enabled' : 'Disabled'}
              labelPlacement="end"
            />
          </Box>
        </Paper>

        <Divider sx={{ my: 3 }} />

        {/* Appointment Reminders */}
        <Paper sx={{ p: 3, mb: 3, bgcolor: 'background.default' }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
              <CalendarIcon color="primary" />
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6" gutterBottom>
                  Auto-Send Appointment Reminders
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Automatically send email reminders to customers before scheduled appointments.
                </Typography>
                {formData.autoSendAppointmentReminders && (
                  <TextField
                    label="Reminder Time"
                    type="number"
                    value={formData.appointmentReminderHours}
                    onChange={(e) => handleChange('appointmentReminderHours', parseInt(e.target.value) || 24)}
                    inputProps={{ min: 1, max: 168 }}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">hours before</InputAdornment>
                    }}
                    helperText="Send reminder X hours before the scheduled appointment"
                    size="small"
                    sx={{ maxWidth: 300 }}
                  />
                )}
              </Box>
            </Box>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.autoSendAppointmentReminders}
                  onChange={(e) => handleChange('autoSendAppointmentReminders', e.target.checked)}
                  color="primary"
                />
              }
              label={formData.autoSendAppointmentReminders ? 'Enabled' : 'Disabled'}
              labelPlacement="end"
            />
          </Box>
        </Paper>

        <Divider sx={{ my: 3 }} />

        {/* Estimate Follow-Up */}
        <Paper sx={{ p: 3, mb: 3, bgcolor: 'background.default' }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
              <EmailIcon color="primary" />
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6" gutterBottom>
                  Auto-Send Estimate Follow-Up Emails
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Automatically send follow-up emails to customers who haven't responded to estimates.
                </Typography>
                {formData.autoSendEstimateFollowUp && (
                  <TextField
                    label="Follow-Up Delay"
                    type="number"
                    value={formData.estimateFollowUpDays}
                    onChange={(e) => handleChange('estimateFollowUpDays', parseInt(e.target.value) || 3)}
                    inputProps={{ min: 1, max: 30 }}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">days after estimate</InputAdornment>
                    }}
                    helperText="Send follow-up email X days after estimate is sent"
                    size="small"
                    sx={{ maxWidth: 300 }}
                  />
                )}
              </Box>
            </Box>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.autoSendEstimateFollowUp}
                  onChange={(e) => handleChange('autoSendEstimateFollowUp', e.target.checked)}
                  color="primary"
                />
              }
              label={formData.autoSendEstimateFollowUp ? 'Enabled' : 'Disabled'}
              labelPlacement="end"
            />
          </Box>
        </Paper>

        <Divider sx={{ my: 3 }} />

        {/* Auto-Generate Invoices */}
        <Paper sx={{ p: 3, mb: 3, bgcolor: 'background.default' }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
              <ReceiptIcon color="primary" />
              <Box>
                <Typography variant="h6" gutterBottom>
                  Auto-Generate Invoices from Completed Jobs
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Automatically generate and email invoices when a job status is set to "Completed".
                </Typography>
              </Box>
            </Box>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.autoGenerateInvoiceOnCompletion}
                  onChange={(e) => handleChange('autoGenerateInvoiceOnCompletion', e.target.checked)}
                  color="primary"
                />
              }
              label={formData.autoGenerateInvoiceOnCompletion ? 'Enabled' : 'Disabled'}
              labelPlacement="end"
            />
          </Box>
        </Paper>

        <Button
          type="submit"
          variant="contained"
          color="primary"
          fullWidth
          size="large"
          sx={{ mt: 3 }}
        >
          Save Automation Settings
        </Button>
      </form>
    </Paper>
  );
}
